/**
 * Доска диспетчера — канбан развозки.
 *
 * Колонка слева: заказы дня, которые ещё никуда не поставлены. Дальше —
 * по колонке на машину. Заказ переносится мышкой: из нераспределённых в
 * машину, между машинами, обратно в нераспределённые. Это и есть сборка
 * маршрута; таблица с галочками на «Заказах» остаётся для массового выбора.
 *
 * Чего доска намеренно не умеет: переводить заказ в «доставлен». Доставка —
 * это тара, деньги и подпись клиента, она проводится водителем или оператором
 * в карточке, а не перетаскиванием карточки мышкой.
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, money, todayIso } from '../api';
import { useApi } from '../useApi';
import { ErrorBox, Loading, Status, useToast } from '../ui';

type Client = { id: string; name: string; phone: string; cachedBalanceKop: string };
type Address = { street: string; house: string; flat: string | null; comment: string | null };

type BoardOrder = {
  id: string;
  number: string;
  status: string;
  timeFrom: string | null;
  timeTo: string | null;
  totalKop: string;
  plannedGive: number;
  plannedTake: number;
  client: Client;
  address: Address;
  routeStop: { route: { id: string } } | null;
};

type BoardRoute = {
  id: string;
  status: string;
  vehicle: string | null;
  loadedFull: number;
  driver: { id: string; name: string } | null;
  stops: Array<{
    id: string;
    position: number;
    doneAt: string | null;
    plannedGive: number;
    plannedTake: number;
    order: BoardOrder & { delivery: { givenFull: number; takenEmpty: number } | null };
  }>;
};

/** Что именно тащим: заказ и то, откуда он снялся (если снялся). */
type Dragged = { orderId: string; stopId: string | null; fromRouteId: string | null };

export function Board() {
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? todayIso();
  const [drag, setDrag] = useState<Dragged | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const orders = useApi<{ items: BoardOrder[] }>(`/orders?date=${date}&limit=500`);
  const routes = useApi<BoardRoute[]>(`/routes?date=${date}`);
  const drivers = useApi<Array<{ id: string; name: string; role: string; active: boolean }>>('/users');

  // «Не распределены» — это заказы, которые ещё ждут машину. Заказ, закрытый
  // оператором вручную мимо маршрута, в этой колонке только мешал бы: увезти
  // его уже некуда, а перетащить нельзя.
  const all = orders.data?.items ?? [];
  const free = all.filter((o) => !o.routeStop && ['NEW', 'PLANNED'].includes(o.status));
  const offRoute = all.filter((o) => !o.routeStop && !['NEW', 'PLANNED'].includes(o.status));
  const reload = async () => {
    await Promise.all([orders.reload(), routes.reload()]);
  };

  /** Любое действие доски: занять, выполнить, показать причину отказа, обновить. */
  const act = async (what: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await what();
      await reload();
      toast.show(ok);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Не получилось', 5000);
    } finally {
      setBusy(false);
    }
  };

  const drop = async (targetRouteId: string | null) => {
    const d = drag;
    setDrag(null);
    setOver(null);
    if (!d || d.fromRouteId === targetRouteId) return;

    await act(async () => {
      // Сначала снимаем со старой машины, потом ставим в новую: заказ не может
      // одновременно стоять в двух маршрутах — на точке уникальный orderId.
      if (d.fromRouteId && d.stopId) await api(`/routes/${d.fromRouteId}/stops/${d.stopId}`, { method: 'DELETE' });
      if (targetRouteId) await api(`/routes/${targetRouteId}/stops`, { body: { orderIds: [d.orderId] } });
    }, targetRouteId ? 'Заказ поставлен в маршрут' : 'Заказ снят с маршрута');
  };

  const addRoute = async (driverId: string) =>
    act(() => api('/routes', { body: { date, driverId: driverId || null, allowEmpty: true } }), 'Машина добавлена');

  /** Машину заводят заранее и не всегда угадывают: пустую можно убрать. */
  const dropRoute = async (r: BoardRoute) => {
    if (!confirm(`Убрать пустую машину «${r.driver?.name ?? 'без водителя'}» с доски?`)) return;
    await act(() => api(`/routes/${r.id}`, { method: 'DELETE' }), 'Машина убрана');
  };

  const closeRoute = async (r: BoardRoute) => {
    const empty = prompt(`Сколько пустой тары вернул ${r.driver?.name ?? 'водитель'} на склад?`);
    if (empty === null) return;
    await act(
      () => api(`/routes/${r.id}/close`, { body: { returnedEmpty: Number(empty) } }),
      'Маршрут закрыт',
    );
  };

  const dragProps = (o: BoardOrder, stopId: string | null, fromRouteId: string | null, locked: boolean) =>
    locked
      ? {}
      : {
          draggable: true,
          onDragStart: () => setDrag({ orderId: o.id, stopId, fromRouteId }),
          onDragEnd: () => {
            setDrag(null);
            setOver(null);
          },
        };

  const zoneProps = (id: string, routeId: string | null, accepting: boolean) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!accepting) return;
      e.preventDefault();
      if (over !== id) setOver(id);
    },
    onDragLeave: () => setOver((cur) => (cur === id ? null : cur)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (accepting) void drop(routeId);
    },
    className: `col-body${over === id && accepting ? ' over' : ''}`,
  });

  const loading = orders.loading && routes.loading && !orders.data;
  const error = orders.error ?? routes.error;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Доска развозки</h1>
          <div className="muted">
            Перетащите заказ в машину — так собирается маршрут. Обратно в «Не распределены» — чтобы снять точку.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <input
            type="date"
            value={date}
            style={{ width: 160 }}
            onChange={(e) => setParams({ date: e.target.value }, { replace: true })}
          />
          <select
            defaultValue=""
            style={{ width: 210 }}
            disabled={busy}
            onChange={(e) => {
              const v = e.target.value;
              e.target.value = '';
              if (v) void addRoute(v);
            }}
          >
            <option value="">+ Машина на линию…</option>
            {(drivers.data ?? [])
              .filter((u) => u.role === 'DRIVER' && u.active)
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      <ErrorBox error={error} />
      {loading ? (
        <Loading />
      ) : (
        <div className={`board${busy ? ' busy' : ''}`}>
          {/* ── Нераспределённые ─────────────────────────────────────────── */}
          <section className="col">
            <header className="col-head">
              <div className="col-title">Не распределены</div>
              <div className="col-sub">
                {free.length} шт · {free.reduce((s, o) => s + o.plannedGive, 0)} бут.
                {offRoute.length ? ` · ещё ${offRoute.length} закрыты мимо маршрута` : ''}
              </div>
            </header>
            <div {...zoneProps('free', null, !!drag?.fromRouteId)}>
              {free.length === 0 ? (
                <div className="col-empty">
                  Все заказы дня разложены по машинам.
                  <br />
                  Новые появятся здесь.
                </div>
              ) : (
                free.map((o) => (
                  <article
                    key={o.id}
                    className={`kcard${drag?.orderId === o.id ? ' dragging' : ''}`}
                    {...dragProps(o, null, null, false)}
                  >
                    <OrderFace o={o} />
                  </article>
                ))
              )}
            </div>
          </section>

          {/* ── Машины ───────────────────────────────────────────────────── */}
          {(routes.data ?? []).map((r) => {
            const done = r.stops.filter((s) => s.doneAt).length;
            const sum = r.stops.reduce((s, x) => s + BigInt(x.order.totalKop), 0n);
            const open = r.status !== 'CLOSED';
            return (
              <section className={`col${r.status === 'ACTIVE' ? ' live' : ''}`} data-route={r.id} key={r.id}>
                <header className="col-head">
                  <div className="spread">
                    <div className="col-title">{r.driver?.name ?? 'Без водителя'}</div>
                    <Status value={r.status} />
                  </div>
                  <div className="col-sub">
                    {done}/{r.stops.length} точек · {r.loadedFull} бут. · {money(sum)}
                  </div>
                  <div className="col-actions">
                    {r.status === 'PLANNED' ? (
                      <button
                        disabled={busy || !r.stops.length}
                        onClick={() => void act(() => api(`/routes/${r.id}/start`, { body: {} }), 'Машина в рейсе')}
                      >
                        Выпустить в рейс
                      </button>
                    ) : null}
                    {r.status === 'ACTIVE' ? (
                      <button className="primary" disabled={busy} onClick={() => void closeRoute(r)}>
                        Закрыть маршрут
                      </button>
                    ) : null}
                    {r.status === 'DRAFT' ? <span className="muted">Назначьте водителя на «Маршрутах»</span> : null}
                    {!r.stops.length && (r.status === 'DRAFT' || r.status === 'PLANNED') ? (
                      <button className="ghost" data-drop={r.id} disabled={busy} onClick={() => void dropRoute(r)}>
                        убрать машину
                      </button>
                    ) : null}
                  </div>
                </header>
                <div {...zoneProps(r.id, r.id, open && !!drag && drag.fromRouteId !== r.id)}>
                  {r.stops.length === 0 ? (
                    <div className="col-empty">Машина пустая. Перетащите сюда заказы.</div>
                  ) : (
                    r.stops.map((s) => (
                      <article
                        key={s.id}
                        className={`kcard${s.doneAt ? ' done' : ''}${drag?.orderId === s.order.id ? ' dragging' : ''}`}
                        {...dragProps(s.order, s.id, r.id, !open || !!s.doneAt)}
                      >
                        <span className="kpos">{s.position}</span>
                        <OrderFace o={s.order} />
                        {s.doneAt ? (
                          <div className="kdone">
                            выдано {s.order.delivery?.givenFull ?? 0} · забрано {s.order.delivery?.takenEmpty ?? 0}
                          </div>
                        ) : null}
                      </article>
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {toast.node}
    </>
  );
}

/** Лицо заказа на доске: кому, куда, сколько воды и денег. */
function OrderFace({ o }: { o: BoardOrder }) {
  const debt = BigInt(o.client.cachedBalanceKop) < 0n;
  return (
    <>
      <div className="kname">
        <Link to={`/clients/${o.client.id}`} onClick={(e) => e.stopPropagation()}>
          {o.client.name}
        </Link>
      </div>
      <div className="kaddr">
        {o.address.street}, {o.address.house}
        {o.address.flat ? `, кв. ${o.address.flat}` : ''}
      </div>
      <div className="kaddr">
        <Link className="mono" to={`/orders/${o.id}`} onClick={(e) => e.stopPropagation()}>
          {o.number}
        </Link>
      </div>
      <div className="kmeta">
        <span className="kbottles">
          +{o.plannedGive} / −{o.plannedTake}
        </span>
        <span className="mono">{money(o.totalKop)}</span>
        {o.timeFrom ? <span className="badge">{o.timeFrom.slice(0, 5)}</span> : null}
        {debt ? <span className="badge red">долг</span> : null}
      </div>
    </>
  );
}
